// GOV13 receipts/base-plan polish — pure, truthful receipt-state authority. The backend creates a receipt exactly
// once (only on verified payment AND successful fulfilment); this module maps the canonical receipt fields
// (payment_status + fulfilment_status + refund fields) to ONE of seven distinct display states so the UI never
// claims "fulfilled" for a merely-paid row, never claims "paid" for a pending one, and never renders a failed
// payment as a provider outage. The frontend NEVER creates a receipt object.

export const RECEIPT_STATES = {
  PAID_AND_FULFILLED: 'PAID_AND_FULFILLED',
  PAID_PENDING_FULFILMENT: 'PAID_PENDING_FULFILMENT',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  FULFILMENT_FAILED: 'FULFILMENT_FAILED',
};

const _norm = (v) => String(v || '').trim().toUpperCase();

export function receiptState(r) {
  if (!r) return { state: RECEIPT_STATES.PAYMENT_PENDING, label: 'Payment pending', tone: 'amber', claimsFulfilled: false };
  const pay = _norm(r.payment_status);
  const ful = _norm(r.fulfilment_status);
  const refunded = _norm(r.refund_status) === 'REFUNDED' || r.refunded === true;
  const partial = _norm(r.refund_status) === 'PARTIALLY_REFUNDED' || r.partially_refunded === true;

  if (partial) return { state: RECEIPT_STATES.PARTIALLY_REFUNDED, label: 'Partially refunded', tone: 'purple', claimsFulfilled: false };
  if (refunded) return { state: RECEIPT_STATES.REFUNDED, label: 'Refunded', tone: 'purple', claimsFulfilled: false };

  if (pay === 'PAID') {
    if (ful === 'FULFILLED') return { state: RECEIPT_STATES.PAID_AND_FULFILLED, label: 'Paid · fulfilled', tone: 'green', claimsFulfilled: true };
    if (ful === 'FULFILMENT_FAILED') return { state: RECEIPT_STATES.FULFILMENT_FAILED, label: 'Paid · fulfilment needs attention', tone: 'red', claimsFulfilled: false };
    return { state: RECEIPT_STATES.PAID_PENDING_FULFILMENT, label: 'Paid · processing', tone: 'blue', claimsFulfilled: false };
  }
  if (pay === 'PAYMENT_FAILED' || pay === 'FAILED') return { state: RECEIPT_STATES.PAYMENT_FAILED, label: 'Payment failed', tone: 'red', claimsFulfilled: false };
  return { state: RECEIPT_STATES.PAYMENT_PENDING, label: 'Payment pending', tone: 'amber', claimsFulfilled: false };
}

// Stable dedup key (never combine an attempt row with its receipt as two purchases).
export function receiptKey(r) {
  return r.invoice_number || r.receipt_id || r.payment_transaction_id || r.id;
}

// Dedup by stable id (first occurrence wins) — no duplicate rows after polling/refetch.
export function dedupeReceipts(list) {
  const seen = new Set();
  const out = [];
  for (const r of (list || [])) {
    const k = receiptKey(r);
    if (k == null || seen.has(k)) continue;
    seen.add(k); out.push(r);
  }
  return out;
}

// Deterministic order: newest paid/issued first, then stable id as a tiebreak.
export function sortReceipts(list) {
  return [...(list || [])].sort((a, b) => {
    const ta = String(a.paid_at || a.issued_at || '');
    const tb = String(b.paid_at || b.issued_at || '');
    if (ta !== tb) return tb.localeCompare(ta);
    return String(receiptKey(a)).localeCompare(String(receiptKey(b)));
  });
}

export function prepareReceipts(list) {
  return sortReceipts(dedupeReceipts(list));
}
